// global.js — minimal progressive enhancement (budget ≤150KB, keep it tiny).
// Everything here degrades gracefully: no JS → native forms, /cart page, visible content.

const DRAWER_SECTION = 'cart-drawer';
const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// ---------------------------------------------------------------- screen height
// 24.10.2026 (Alex, iPhone: "warum verändert sich immer das bild beim scrollen?! auf der startseite ist das auch so im hero"):
// the home hero read 100svh since 49423ef and still changed size on scroll on his phone, so svh does not hold there. Heights
// that follow the screen on phones (home hero, product gallery) read --vh0 instead: the window height at load, set again only
// when the width changes (turning the phone), never on a scroll. Without JS the CSS falls back to 100svh.
let vh0Width = window.innerWidth;
document.documentElement.style.setProperty('--vh0', `${window.innerHeight}px`);
window.addEventListener('resize', () => {
  if (window.innerWidth === vh0Width) return;
  vh0Width = window.innerWidth;
  document.documentElement.style.setProperty('--vh0', `${window.innerHeight}px`);
});

// ---------------------------------------------------------------- price note
// L48: every page says "zzgl. Versandkosten" (one cacheable HTML for all visitors). In Germany, once the cart
// total (the drawer carries it, fresh from every cart answer) or the price shown with a note reaches the
// free-shipping threshold, the note switches to "versandkostenfrei" (snippets/price-note).
function syncPriceNotes() {
  const body = document.querySelector('[data-drawer-body]');
  const cartTotal = body ? Number(body.dataset.cartTotal || 0) : 0;
  const country = (window.Shopify && window.Shopify.country) || '';
  document.querySelectorAll('[data-price-note]').forEach((note) => {
    const from = Number(note.dataset.freeFrom || 0);
    const inDe = (country || note.dataset.country) === 'DE';
    const free = from > 0 && inDe && (cartTotal >= from || Number(note.dataset.price || 0) >= from);
    note.querySelectorAll('[data-pn-paid]').forEach((el) => { el.hidden = free; });
    note.querySelectorAll('[data-pn-free]').forEach((el) => { el.hidden = !free; });
  });
}
syncPriceNotes();

// ---------------------------------------------------------------- cart drawer
// Native <dialog> gives focus trap + ESC + top layer (BFSG). We add: slide-in
// animation (skipped under reduced motion), body scroll lock, Section
// Rendering API refresh, qty steppers, remove, header badge sync.
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.addEventListener('click', (e) => this.onClick(e));
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
    this.dialog.addEventListener('mousedown', (e) => { if (e.target === this.dialog) this.close(); });
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-drawer-qty-input]');
      if (!input) return;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.changeLine(this.lineOf(input), qty);
    });
  }
  lineOf(el) {
    const li = el.closest('[data-line]');
    return li ? parseInt(li.dataset.line, 10) : null;
  }
  onClick(e) {
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { this.close(); return; }
    const stepBtn = e.target.closest('[data-drawer-qty]');
    if (stepBtn) {
      const input = stepBtn.closest('[data-drawer-stepper]').querySelector('input');
      const qty = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(stepBtn.dataset.drawerQty, 10));
      this.changeLine(this.lineOf(stepBtn), qty);
      return;
    }
    // quantity tiers (snippets/cart-vol): a tap sets 1, 2 or 3 pieces in place; the link is the no-JS fallback
    const tier = e.target.closest('[data-cvol-qty]');
    if (tier) {
      e.preventDefault();
      if (!tier.classList.contains('is-selected')) this.changeLine(this.lineOf(tier), parseInt(tier.dataset.cvolQty, 10));
      return;
    }
    const removeBtn = e.target.closest('[data-drawer-remove]');
    if (removeBtn) this.changeLine(this.lineOf(removeBtn), 0);
  }
  open(opener) {
    if (this.dialog.open) return;
    this.opener = opener || document.activeElement;
    this.dialog.showModal();
    this.syncAssure();
    document.documentElement.classList.add('drawer-lock');
    if (REDUCE_MOTION.matches) {
      this.dialog.classList.add('is-open');
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
    }
    // the dialog itself takes the focus (tabindex -1): no ring around the close button for touch and mouse; Tab reaches it next
    this.dialog.focus({ preventScroll: true });
  }
  close() {
    if (!this.dialog.open) return;
    const done = () => {
      if (!this.dialog.open) return;
      this.dialog.close();
      document.documentElement.classList.remove('drawer-lock');
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    };
    this.dialog.classList.remove('is-open');
    if (REDUCE_MOTION.matches) { done(); return; }
    this.dialog.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 400); // safety net if transitionend never fires
  }
  renderSection(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const fresh = doc.querySelector('[data-drawer-body]');
    const current = this.querySelector('[data-drawer-body]');
    if (!fresh || !current) return;
    current.replaceWith(fresh);
    this.syncAssure();
    this.syncBadge();
    syncPriceNotes();
    // Replacing the body drops focus — restore it inside the open dialog.
    if (this.dialog.open && !this.dialog.contains(document.activeElement)) this.dialog.focus({ preventScroll: true });
  }
  // Trust tiles under the checkout button (Alex 24.10.2026): the foot first ends at the button, so the line and "Passt dazu"
  // are in view; scrolling down the list slides the tiles in, scrolling back to the top folds them again ("geht nicht wieder
  // runter", Alex). Open from 16 px, folded at 2 px or less, so the change of the list height cannot flicker. A list that does
  // not scroll with the foot folded shows the tiles at once and keeps them.
  syncAssure() {
    const foot = this.querySelector('.cd__foot');
    const scroll = this.querySelector('.cd__lines') ? this.querySelector('.drawer__scroll') : null;
    if (!foot || !scroll) return;
    foot.classList.add('is-collapsible');
    foot.classList.remove('is-open');
    if (!this.dialog.open) return;
    requestAnimationFrame(() => {
      if (scroll.scrollHeight - scroll.clientHeight < 8) { foot.classList.add('is-open'); return; }
      const sync = () => {
        if (scroll.scrollTop > 16) foot.classList.add('is-open');
        else if (scroll.scrollTop <= 2) foot.classList.remove('is-open');
      };
      sync();
      scroll.addEventListener('scroll', sync, { passive: true });
    });
  }
  syncBadge() {
    const body = this.querySelector('[data-drawer-body]');
    const count = body ? parseInt(body.dataset.itemCount || '0', 10) : 0;
    const cartLink = document.querySelector('.site-header__cart');
    if (cartLink) {
      let badge = cartLink.querySelector('.site-header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'site-header__cart-count';
          badge.setAttribute('aria-hidden', 'true');
          cartLink.appendChild(badge);
        }
        badge.textContent = count > 99 ? '99+' : String(count);
      } else if (badge) {
        badge.remove();
      }
      const sr = cartLink.querySelector('.visually-hidden');
      sr && (sr.textContent = document.documentElement.lang.startsWith('en') ? `Cart (${count} items)` : `Warenkorb (${count} Artikel)`);
    }
    const status = this.querySelector('[data-drawer-status]');
    status && (status.textContent = document.documentElement.lang.startsWith('en') ? `Cart updated: ${count} items` : `Warenkorb aktualisiert: ${count} Artikel`);
  }
  setBusy(busy) {
    const body = this.querySelector('[data-drawer-body]');
    body && body.toggleAttribute('data-busy', busy);
  }
  async changeLine(line, quantity) {
    if (!line) return;
    this.setBusy(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: DRAWER_SECTION }),
      });
      if (!res.ok) throw new Error('change failed');
      const data = await res.json();
      if (data.sections && data.sections[DRAWER_SECTION]) this.renderSection(data.sections[DRAWER_SECTION]);
    } catch (err) {
      window.location.href = '/cart'; // graceful fallback: the full cart page
    } finally {
      this.setBusy(false);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

// Header cart icon opens the drawer (href="/cart" stays as no-JS fallback).
document.querySelectorAll('.site-header__cart').forEach((link) => {
  link.addEventListener('click', (e) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    e.preventDefault();
    drawer.open(link);
  });
});

// --------------------------------------------------------------- mobile nav
// Native <dialog> = focus trap + ESC + top layer (BFSG). Opens < 920px only.
// No JS → the burger is inert; footer + in-page links still reach every page.
class MobileNav extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.opener = document.querySelector('[data-mobile-nav-open]');
    this.opener && this.opener.addEventListener('click', () => this.open());
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
    this.dialog.addEventListener('mousedown', (e) => { if (e.target === this.dialog) this.close(); });
    this.querySelectorAll('[data-mobile-nav-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
    // Any nav link click closes the drawer before navigating.
    this.querySelectorAll('a[href]').forEach((a) => a.addEventListener('click', () => this.close()));
    // Drill-down panels. The root list stays in the DOM underneath, so going back is
    // instant and the scroll position of each level is preserved.
    this.opened = null;
    this.querySelectorAll('[data-mnav-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openPanel(btn.getAttribute('data-mnav-open'), btn));
    });
    this.querySelectorAll('[data-mnav-back]').forEach((btn) => btn.addEventListener('click', () => this.closePanel()));
  }
  openPanel(key, opener) {
    const panel = this.querySelector(`[data-mnav-panel="${key}"]`);
    if (!panel) return;
    this.closePanel(true);
    this.opened = { panel, opener };
    panel.hidden = false;
    opener && opener.setAttribute('aria-expanded', 'true');
    const show = () => {
      panel.classList.add('is-open');
      const back = panel.querySelector('[data-mnav-back]');
      back && back.focus();
    };
    if (REDUCE_MOTION.matches) show();
    else requestAnimationFrame(() => requestAnimationFrame(show));
  }
  closePanel(silent) {
    if (!this.opened) return;
    const { panel, opener } = this.opened;
    this.opened = null;
    opener && opener.setAttribute('aria-expanded', 'false');
    if (!silent && opener && document.contains(opener)) opener.focus();
    const done = () => { panel.hidden = true; };
    panel.classList.remove('is-open');
    if (REDUCE_MOTION.matches || silent) { done(); return; }
    panel.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 400); // safety net if transitionend never fires
  }
  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.classList.add('drawer-lock');
    this.opener && this.opener.setAttribute('aria-expanded', 'true');
    if (REDUCE_MOTION.matches) {
      this.dialog.classList.add('is-open');
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
    }
    const closeBtn = this.dialog.querySelector('[data-mobile-nav-close]');
    closeBtn && closeBtn.focus();
  }
  close() {
    if (!this.dialog.open) return;
    const done = () => {
      if (!this.dialog.open) return;
      this.dialog.close();
      document.documentElement.classList.remove('drawer-lock');
      this.opener && this.opener.setAttribute('aria-expanded', 'false');
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    };
    this.dialog.classList.remove('is-open');
    if (REDUCE_MOTION.matches) { done(); return; }
    this.dialog.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 400); // safety net if transitionend never fires
  }
}
customElements.define('mobile-nav', MobileNav);

/* Desktop dropdowns. CSS :hover cannot drive these: the pointer has to cross the strip
   between the trigger and the panel, and a closed panel is not hoverable, so the menu
   snaps shut on the way down. The trigger now fills the header height (no gap left to
   cross) and this element keeps the panel open for a grace period after the pointer
   leaves, which is what makes the diagonal move to a far column survive. */
const MEGA_OPEN_DELAY = 70;   // ignore a pointer merely sweeping across the bar
const MEGA_CLOSE_DELAY = 240; // survive a slip off the edge on the way to the panel
const MEGA_SWAP_DELAY = 120;  // let the pointer skim past a neighbour on its way down

class MegaNav extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-mega-item]'));
    if (!this.items.length) return;
    this.current = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.ignoreFocus = false;
    // Tells the stylesheet that state is ours now. Until this lands, :focus-within is
    // what opens a panel, so the menu still works for a keyboard if the script fails.
    this.classList.add('is-ready');
    this.items.forEach((li) => {
      li.addEventListener('mouseenter', () => this.intentOpen(li));
      li.addEventListener('mouseleave', () => this.intentClose());
      // The panel is a child of the item in the DOM but sits outside its box on screen.
      // Listening on the panel as well makes the hand-off independent of that geometry.
      const panel = li.querySelector('.megamenu, .site-header__sub');
      if (panel) {
        panel.addEventListener('mouseenter', () => this.intentOpen(li));
        panel.addEventListener('mouseleave', () => this.intentClose());
      }
      li.addEventListener('focusin', () => { if (!this.ignoreFocus) this.open(li); });
      li.addEventListener('focusout', (e) => {
        if (!li.contains(e.relatedTarget)) this.close(li);
      });
      const trigger = li.querySelector(':scope > a');
      if (!trigger) return;
      // Touch and pen have no hover: the first tap opens the panel, the second follows
      // the link. Without this the panel would flash and the page would navigate away.
      trigger.addEventListener('click', (e) => {
        if (window.matchMedia('(hover: hover)').matches) return;
        if (this.current !== li) { e.preventDefault(); this.open(li); }
      });
    });
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.current) return;
      const li = this.current;
      this.close(li);
      const trigger = li.querySelector(':scope > a');
      if (!trigger) return;
      // Moving focus back to the trigger fires focusin, which would reopen what Escape
      // just closed. Deafen the handler for that one turn of the event loop.
      this.ignoreFocus = true;
      trigger.focus();
      requestAnimationFrame(() => { this.ignoreFocus = false; });
    });
  }
  intentOpen(li) {
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);
    // Swapping to a neighbour waits too. The path from a trigger down to its own panel
    // passes straight over the next trigger in the bar, and swapping on contact stole
    // the panel the pointer was already travelling to.
    const delay = this.current && this.current !== li ? MEGA_SWAP_DELAY : MEGA_OPEN_DELAY;
    this.openTimer = setTimeout(() => this.open(li), delay);
  }
  intentClose() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeAll(), MEGA_CLOSE_DELAY);
  }
  open(li) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    if (this.current === li) return;
    if (this.current) this.close(this.current);
    this.current = li;
    li.classList.add('is-open');
    const trigger = li.querySelector(':scope > a');
    trigger && trigger.setAttribute('aria-expanded', 'true');
  }
  close(li) {
    li.classList.remove('is-open');
    const trigger = li.querySelector(':scope > a');
    trigger && trigger.setAttribute('aria-expanded', 'false');
    if (this.current === li) this.current = null;
  }
  closeAll() {
    this.items.forEach((li) => this.close(li));
  }
}
customElements.define('mega-nav', MegaNav);


// ------------------------------------------------------------- product form
// AJAX add-to-cart. With a drawer present: refresh + slide in. Without: /cart.
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }
  async onSubmit(e) {
    e.preventDefault();
    const btn = this.form.querySelector('[name="add"]');
    const drawer = document.querySelector('cart-drawer');
    btn && btn.setAttribute('aria-busy', 'true');
    try {
      const payload = Object.fromEntries(new FormData(this.form));
      if (drawer) payload.sections = DRAWER_SECTION;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('add failed');
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:added', {
        detail: { id: payload.id, quantity: parseInt(payload.quantity, 10) || 1, item: data },
      }));
      if (drawer && data.sections && data.sections[DRAWER_SECTION]) {
        drawer.renderSection(data.sections[DRAWER_SECTION]);
        drawer.open(btn);
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      this.form.submit(); // graceful fallback to native submit
    } finally {
      btn && btn.removeAttribute('aria-busy');
    }
  }
}
customElements.define('product-form', ProductForm);

// ------------------------------------------------------------- variant tiles
// PDP variant tiles are radio inputs (name="id"); price markup per variant is
// pre-rendered by Liquid into <template data-variant-price="{id}"> so JS never
// formats money. No JS → the checked radio still posts correctly.
document.querySelectorAll('[data-variant-tiles]').forEach((tiles) => {
  const scope = tiles.closest('section') || document;
  tiles.addEventListener('change', (e) => {
    const radio = e.target.closest('input[type="radio"][name="id"]');
    if (!radio) return;
    // Quantity cards (snippets/qty-cards, 18.10.): one variant, several cards; data-key names each card's
    // templates and data-qty fills the hidden quantity field. Plain tiles have neither.
    const key = radio.dataset.key || radio.value;
    const qtyInput = tiles.querySelector('[data-qty-input]');
    if (qtyInput) qtyInput.value = radio.dataset.qty || '1';
    tiles.querySelectorAll('.tile, .qc').forEach((t) => t.classList.toggle('is-selected', t.contains(radio)));
    const tpl = scope.querySelector(`template[data-variant-price="${key}"]`);
    scope.querySelectorAll('[data-price-target]').forEach((target) => {
      if (tpl) target.innerHTML = tpl.innerHTML;
    });
    const stickyTpl = scope.querySelector(`template[data-variant-sticky="${key}"]`);
    document.querySelectorAll('[data-sticky-price]').forEach((target) => {
      if (stickyTpl) target.innerHTML = stickyTpl.innerHTML;
    });
    // Stock line follows the variant too (audit A06): pre-rendered per variant by Liquid.
    const stockTpl = scope.querySelector(`template[data-variant-stock="${key}"]`);
    scope.querySelectorAll('[data-stock-target]').forEach((target) => {
      if (stockTpl) target.innerHTML = stockTpl.innerHTML;
    });
    const available = radio.dataset.available === 'true';
    // Sold-out size: the notify form takes the buy block's place (snippets/back-in-stock). One
    // <template data-bis-variant> per sold-out size carries its sentence and hidden fields.
    const bisTpl = scope.querySelector(`template[data-bis-variant="${radio.value}"]`);
    scope.querySelectorAll('[data-bis]').forEach((bis) => {
      bis.hidden = available;
      const slot = bis.querySelector('[data-bis-slot]');
      if (slot && bisTpl) slot.innerHTML = bisTpl.innerHTML;
    });
    scope.querySelectorAll('[data-buy-block]').forEach((block) => { block.hidden = !available; });
    const notify = !available && !!scope.querySelector('[data-bis]');
    document.querySelectorAll('[data-buy-button], .sticky-atc [name="add"]').forEach((buyBtn) => {
      // The bar's button points to the notify form instead of sitting there disabled.
      const asNotify = notify && !buyBtn.matches('[data-buy-button]') && !!buyBtn.dataset.labelNotify;
      buyBtn.disabled = !available && !asNotify;
      buyBtn.toggleAttribute('data-notify', asNotify);
      if (!buyBtn.matches('[data-buy-button]')) buyBtn.type = asNotify ? 'button' : 'submit';
      // Label and price are separate nodes: the money string is formatted by
      // Liquid and carried on the radio, so JS never builds a price itself.
      // Labels come from the locale via data-label-* (audit A07: /en showed German after a switch).
      const text = asNotify ? buyBtn.dataset.labelNotify : available ? (buyBtn.dataset.labelAdd || 'In den Warenkorb') : (buyBtn.dataset.labelSoldout || 'Ausverkauft');
      const label = buyBtn.querySelector('[data-buy-label]');
      if (label) label.textContent = text;
      else buyBtn.textContent = text;
      const priceEl = buyBtn.querySelector('[data-buy-price]');
      if (priceEl) {
        priceEl.textContent = available ? (radio.dataset.price || '') : '';
        priceEl.hidden = !available;
      }
    });
    // The chosen size's own image in the gallery and the buy bar (L18): the radio names it
    // (snippets/pdp-hero-media); the gallery thumb does the swap, so zoom and swipe follow.
    if (radio.dataset.mediaId) {
      const thumb = document.querySelector(`[data-gallery-thumb][data-media-id="${radio.dataset.mediaId}"]`);
      if (thumb && !thumb.classList.contains('is-active')) thumb.click();
      const barImg = document.querySelector('.sticky-atc__img img');
      if (barImg && radio.dataset.mediaBar) {
        barImg.removeAttribute('srcset');
        barImg.src = radio.dataset.mediaBar;
      }
    }
    // The price note of the chosen size (buy box, buy bar) judges free shipping by that size's price (L48).
    if (radio.dataset.cents) {
      document.querySelectorAll('[data-price-note][data-pn-variant]').forEach((note) => { note.dataset.price = radio.dataset.cents; });
      syncPriceNotes();
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', radio.value);
    history.replaceState({}, '', url);
  });
});

// ------------------------------------------------------------- misc UI
// Header shadow on scroll (design system v5-A1).
const header = document.querySelector('.site-header');
if (header) {
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// Partner line (snippets/partner-line, Alex 24.10.2026): the logos move right to left on their own and follow a drag with the
// mouse or a finger (sideways only; touch-action pan-y leaves the page scroll to the browser). The track holds the list twice,
// so the offset wraps at one list width without a seam. Arrow keys move it too. No frames off screen or in a hidden tab;
// reduced motion: no movement on its own.
document.querySelectorAll('[data-partner-line]').forEach((vp) => {
  const track = vp.querySelector('.hero-partners__track');
  const list = track && track.querySelector('.hero-partners__list');
  if (!track || !list) return;
  const SPEED = 24; // px per second
  const PAUSE_AFTER = 1500; // ms without movement after a drag or a key
  let x = 0;
  let span = 0;
  let last = 0;
  let raf = 0;
  let visible = false;
  let drag = null;
  let resumeAt = 0;
  const wrap = () => {
    if (span <= 0) return;
    x %= span;
    if (x > 0) x -= span;
  };
  const paint = () => { track.style.transform = `translate3d(${x}px, 0, 0)`; };
  const tick = (t) => {
    raf = 0;
    if (!visible || document.hidden) return;
    const dt = last ? Math.min(t - last, 100) : 0;
    last = t;
    if (!drag && !REDUCE_MOTION.matches && t >= resumeAt) {
      x -= (SPEED * dt) / 1000;
      wrap();
      paint();
    }
    raf = requestAnimationFrame(tick);
  };
  const start = () => {
    if (raf || !visible || document.hidden) return;
    last = 0;
    raf = requestAnimationFrame(tick);
  };
  const measure = () => {
    span = list.getBoundingClientRect().width;
    wrap();
    paint();
  };
  vp.classList.add('is-js');
  measure();
  new ResizeObserver(measure).observe(list);
  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    start();
  }).observe(vp);
  document.addEventListener('visibilitychange', start);
  vp.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    drag = { id: e.pointerId, from: e.clientX, start: x, moved: false };
  });
  vp.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.from;
    if (!drag.moved && Math.abs(dx) > 4) {
      drag.moved = true;
      vp.setPointerCapture(e.pointerId);
      vp.classList.add('is-dragging');
    }
    if (!drag.moved) return;
    x = drag.start + dx;
    wrap();
    paint();
  });
  const endDrag = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    drag = null;
    vp.classList.remove('is-dragging');
    resumeAt = performance.now() + PAUSE_AFTER;
  };
  vp.addEventListener('pointerup', endDrag);
  vp.addEventListener('pointercancel', endDrag);
  vp.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    x += e.key === 'ArrowLeft' ? 120 : -120;
    wrap();
    paint();
    resumeAt = performance.now() + PAUSE_AFTER;
  });
});

// Sticky ATC bar — shows once the buy button leaves the viewport (PDP).
const stickyAtc = document.querySelector('[data-sticky-atc]');
const buyButton = document.querySelector('[data-buy-button]');
if (stickyAtc && buyButton) {
  // Scroll-position check (rAF-throttled). An IntersectionObserver alone
  // silently dies when the observed node is detached by a DOM swap, and
  // misses instant jumps (keyboard End, anchor links) — this does not.
  let atcTick = false;
  const updateSticky = () => {
    atcTick = false;
    // A sold-out size hides the buy button; the notify form stands in its place, so the bar follows
    // that form. Measuring the hidden button (all zeros) kept the bar hidden for good.
    const notifyForm = buyButton.getClientRects().length ? null : document.querySelector('[data-bis]:not([hidden])');
    const rect = (notifyForm || buyButton).getBoundingClientRect();
    // L10 (lead 17.10.): only once price AND buy button have left the view at the top (behind the sticky header
    // counts), and never on the buy box. The bar used to show while the button was still below the first screen
    // (2026-09-29) and then covered price, base price or stock line at 414x715, 960x600, 1280x585 and others.
    const box = buyButton.closest('.product-biocide__info, .product-feed__info, .product-test__info');
    const price = box && box.querySelector('[data-price-target], .buybox__price-row');
    const head = document.querySelector('.site-header');
    const viewTop = Math.max(0, head ? head.getBoundingClientRect().bottom : 0);
    const tile = stickyAtc.querySelector('.sticky-atc__img');
    const tileOut = tile && tile.getClientRects().length ? Math.max(0, -parseFloat(getComputedStyle(tile).marginTop) || 0) : 0;
    const barTop = window.innerHeight - stickyAtc.offsetHeight - tileOut;
    const gone = rect.bottom <= viewTop && (!price || price.getBoundingClientRect().bottom <= viewTop);
    const clearOfBox = !box || box.getBoundingClientRect().bottom <= barTop;
    // Alex 2026-10-15: gone from the footer help block (photo, phone, email) down; the footer
    // carries its own biocide note and legal links from there on
    const footerStart = document.querySelector('.site-footer__help, .site-footer');
    const atFooter = !!footerStart && footerStart.getBoundingClientRect().top < window.innerHeight;
    const show = gone && clearOfBox && !atFooter;
    stickyAtc.classList.toggle('is-visible', show);
    stickyAtc.setAttribute('aria-hidden', String(!show));
    // Reserve the bar's height at the page end while it shows: on biocide PDPs it is up
    // to ~150px tall on phones and covered the last footer row (the legal links).
    document.body.style.paddingBottom = show ? `${stickyAtc.offsetHeight}px` : '';
    // The Shopify Inbox chat button (app embed, bottom right) sat on this bar at 375/768/1280 and
    // covered the warning and the buy button (measured 2026-10-09, AE1). Lift it above the bar.
    document.documentElement.style.setProperty('--shopify-chat-activator-offset', show ? `${stickyAtc.offsetHeight + 12}px` : '');
  };
  window.addEventListener('scroll', () => {
    if (!atcTick) { atcTick = true; requestAnimationFrame(updateSticky); }
  }, { passive: true });
  updateSticky();
  // Notify mode (sold-out size): the bar's button brings the buyer to the email field.
  stickyAtc.addEventListener('click', (e) => {
    const btn = e.target.closest('[name="add"][data-notify]');
    const form = btn && document.querySelector('[data-bis]:not([hidden])');
    if (!form) return;
    e.preventDefault();
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    form.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
    const field = form.querySelector('input[type="email"]');
    if (field) field.focus({ preventScroll: true });
  });
}

// Floating app buttons may never sit on regulated info or a buy button (CLAUDE.md: never bury).
// Inbox chat (app embed, bottom right): with the buy bar hidden it covered up to 9550 px² of the
// buy-box biocide warning and part of "In den Warenkorb" (measured 2026-10-09, AE1). base.css keeps
// it hidden until this check marks it clear, and it steps aside while it would overlap one of
// these. An open chat is never hidden.
// Pandectes cookie icon (reopens the consent settings, bottom left): covered 1936 px² of the bar's
// biocide warning at 375 (AE1). It rides above the buy bar and steps aside the same way. The consent
// window keeps its content; only its place and tap sizes change (CMP_BAR and placeCmp below).
// Every other buy and checkout button and VAT note too: chat or icon sat on card "In den Warenkorb",
// "Größe wählen", set cards, "Passendes Set finden", "Zur Kasse", "Hinzufügen" and the home tax line at
// 118 (chat) and 68 (icon) of 9603 scroll positions, 46 pages x 3 widths x 2 engines (2026-10-09).
const NEVER_COVER = '.biocide-warning, .price, .price-note, .base-price, .hazard-info, .buybox__stock, .buybox__taxnote, .sticky-atc__note, .feed-boundary, .advisory-gate, [data-buy-button], .bis__submit, .sticky-atc [name="add"], [name="add"], [name="checkout"], .product-card__atc, .sp-card__atc, .cx__add, a[href$="#sets"], .cd__tax, .cp__tax, .final-cta__fud';
const coversRegulated = (box) => [...document.querySelectorAll(NEVER_COVER)].some((el) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top;
});
// Running text too (seo-audit M-11, 2026-10-14): above the buy bar the chat still sat on list and
// paragraph text ("Alles für zwei Te…" on the Schnelltest at 375, a home paragraph at 768). The
// elements under a 10 px grid of points are collected, and their own text lines are measured: only
// a real line box under the button counts, so it still shows over photos, bands and gaps.
const overlaps = (r, box) => r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top;
// points every 10 px plus the far edge, so a line that starts under the last few pixels still counts
const edgeSteps = (a, b) => { const pts = []; for (let v = a + 1; v < b - 1; v += 10) pts.push(v); pts.push(b - 1); return pts; };
const coversText = (box) => {
  const seen = new Set();
  for (const x of edgeSteps(box.left, box.right)) {
    for (const y of edgeSteps(box.top, box.bottom)) {
      for (const el of document.elementsFromPoint(x, y)) {
        if (seen.has(el) || el.closest('shopify-chat, pandectes-cmp, [data-consent]')) continue;
        seen.add(el);
        for (const n of el.childNodes) {
          if (n.nodeType !== 3 || !n.nodeValue.trim()) continue;
          const range = document.createRange();
          range.selectNodeContents(n);
          if ([...range.getClientRects()].some((t) => overlaps(t, box))) return true;
        }
      }
    }
  }
  return false;
};
// Inbox now nests its button one shadow root deeper (shopify-agent-runtime, part "activator",
// seen 2026-10-09 16:40); the old top-level search found nothing and the chat stayed unchecked.
const findChatButton = (root) => {
  if (!root) return null;
  const own = root.querySelector('[part~="activator"]') || [...root.querySelectorAll('button')].find((b) => b.offsetWidth);
  if (own) return own;
  for (const el of root.querySelectorAll('*')) {
    const deep = el.shadowRoot && findChatButton(el.shadowRoot);
    if (deep) return deep;
  }
  return null;
};
let chatBtn = null;
let floatTick = false;
let chatReady = false; // the chat button scales in after the element is defined; judge only its final box
const guardFloats = () => {
  floatTick = false;
  const chat = document.querySelector('shopify-chat');
  if (chat && !(chatBtn && chatBtn.isConnected)) chatBtn = findChatButton(chat.shadowRoot);
  const btn = chat && chatBtn;
  const cmp = document.querySelector('pandectes-cmp');
  // the open consent window (a bottom bar on phones) counts too: the chat sat on "Ablehnen". The theme's own banner
  // (snippets/consent) the same way.
  const consent = (cmp && cmp.shadowRoot && cmp.shadowRoot.querySelector('dialog.notice[open]')) || document.querySelector('[data-consent-notice]:not([hidden])');
  if (btn) {
    const box = btn.getBoundingClientRect();
    const c = consent && consent.getBoundingClientRect();
    const onConsent = c && c.width > 0 && c.left < box.right && c.right > box.left && c.top < box.bottom && c.bottom > box.top;
    const clear = chatReady && (chat.hasAttribute('open') || (!onConsent && !coversRegulated(box) && !coversText(box)));
    chat.toggleAttribute('data-chat-clear', clear);
    chat.inert = !clear; // the hidden button must not take keyboard focus either
  }
  const icon = cmp && cmp.shadowRoot && cmp.shadowRoot.querySelector('.widget');
  // Alex 2026-10-15: no floating cookie button in the new theme. The footer link "Cookie-Einstellungen" opens the
  // same window by clicking this button, so it stays in the DOM and never shows.
  if (icon) icon.style.display = 'none';
};
// footer "Cookie-Einstellungen" (data-cookie-settings): opens the Pandectes preferences through the hidden button
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-cookie-settings]');
  if (!link) return;
  const cmp = document.querySelector('pandectes-cmp');
  const widget = cmp && cmp.shadowRoot && cmp.shadowRoot.querySelector('.widget');
  if (!widget) return;
  event.preventDefault();
  widget.click();
});
const queueFloatGuard = () => {
  if (!floatTick) { floatTick = true; requestAnimationFrame(guardFloats); }
};
['scroll', 'resize', 'load'].forEach((type) => window.addEventListener(type, queueFloatGuard, { passive: true }));
// side-scrolling rows ("Einzeln dazu" on phones) move their buy buttons without a page scroll
document.addEventListener('scroll', queueFloatGuard, { capture: true, passive: true });
// When the buy bar toggles, updateSticky moves the chat. WebKit reports the chat's new box one frame
// late, so the same-frame check saw the old box and left the chat on "In den Warenkorb" (measured
// 2026-10-09, iPhone engine). Judge again two frames later.
if (stickyAtc) new MutationObserver(() => requestAnimationFrame(queueFloatGuard)).observe(stickyAtc, { attributes: true, attributeFilter: ['class'] });
// the theme's own consent notice (snippets/consent) opens, moves and closes without a scroll; judge the chat again then
const ownConsent = document.querySelector('[data-consent-notice]');
if (ownConsent) new MutationObserver(queueFloatGuard).observe(ownConsent, { attributes: true, attributeFilter: ['hidden', 'data-place'] });
// the scale-in takes ~320ms and starts ~50ms after the element is defined (measured)
customElements.whenDefined('shopify-chat').then(() => setTimeout(() => { chatReady = true; queueFloatGuard(); }, 600));
// The cookie icon is inserted right after a consent choice, or on load for returning visitors.
// Pandectes creates its host only after defining the element, so wait for the host first.
// Phones: the consent window stood mid-screen on the first visit and covered photo, title and price
// at 375 (theme-cro B3). It becomes a bar at the bottom edge, above the buy bar; every text, link and
// button stays as Pandectes draws it, and it stays open until a choice. It lives in Pandectes' own
// shadow root, which base.css cannot reach, so the rule goes in there.
// L22 (2026-10-17): before the choice the window leaves the page title, the price and the first button free on
// every size (it stood on title and price at 375 x 667 and mid-page from 621 px), and every button and link is at
// least 44 px tall (Pandectes drew 36 to 38 px buttons and 18 to 21 px links). Phones get a bar at the bottom or
// top edge with the three buttons in one row; sideways phones a bar of at most half the screen whose buttons stay
// in view while the text scrolls; wider screens a card in a corner. placeCmp keeps the first place that covers none
// of them. Texts stay as Pandectes draws them.
const CMP_SEL = '.dialog.notice.middle.center[open]';
const CMP_BAR = [
  '.dialog.notice[open] .actions button { min-height: 44px; }',
  '.dialog.notice[open] .links { display: flex; flex-wrap: wrap; column-gap: 16px; }',
  '.dialog.notice[open] .links a { display: inline-flex; align-items: center; min-height: 44px; }',
  `@media (max-width: 620px) { ${CMP_SEL} { --translate-x: 0px; --translate-y: 0px; --padding: 14px; top: auto; bottom: 0; left: 0; width: 100%; max-width: none !important; border-radius: 16px 16px 0 0; z-index: 2147483001; } ${CMP_SEL} .actions { display: flex; flex-direction: row; gap: 8px; } ${CMP_SEL} .actions .group { display: contents; } ${CMP_SEL} .actions button { flex: 1 1 0; min-width: 0; margin: 0; padding-inline: 4px; } }`,
  `@media (min-width: 621px) { ${CMP_SEL} { --translate-x: 0px; --translate-y: 0px; top: auto; bottom: 16px; left: 16px; right: auto; width: 360px; max-width: calc(100% - 32px) !important; z-index: 2147483001; } }`,
  `@media (max-height: 500px) and (min-width: 560px) { ${CMP_SEL} { --padding: 14px; top: auto; bottom: 0; left: 0; right: auto; width: 100%; max-width: none !important; max-height: 50vh; overflow-y: auto; border-radius: 16px 16px 0 0; } ${CMP_SEL} .inner { display: grid; grid-template-columns: minmax(0, 1fr) 300px; column-gap: 24px; align-items: center; } ${CMP_SEL} .inner > * { grid-column: 1; } ${CMP_SEL} .inner > .actions { grid-column: 2; grid-row: 1 / span 3; align-self: start; position: sticky; top: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 0; } ${CMP_SEL} .actions .group { display: contents; } ${CMP_SEL} .actions button { margin: 0; width: 100%; } ${CMP_SEL} .actions button.preferences { grid-column: 1 / -1; } }`,
].join(' ');
let cmpPlacedFor = null;
const placeCmp = (root) => {
  const dialog = root.querySelector('dialog.notice[open]');
  if (!dialog) return;
  cmpPlacedFor = dialog;
  let pos = root.querySelector('style[data-theme-pos]');
  if (!pos) { pos = document.createElement('style'); pos.dataset.themePos = ''; root.append(pos); }
  const bar = window.matchMedia('(max-width: 620px), (max-height: 500px) and (min-width: 560px)').matches;
  const sticky = document.querySelector('[data-sticky-atc].is-visible');
  const lift = !bar && sticky ? sticky.getBoundingClientRect().height : 0;
  const places = bar
    ? ['', `${CMP_SEL} { top: 0; bottom: auto; border-radius: 0 0 16px 16px; }`]
    : [lift ? `${CMP_SEL} { bottom: ${16 + lift}px; }` : '', `${CMP_SEL} { left: auto; right: 16px; bottom: ${16 + lift}px; }`, `${CMP_SEL} { left: auto; right: 16px; top: 16px; bottom: auto; }`, `${CMP_SEL} { top: 16px; bottom: auto; }`];
  const inView = (el) => { if (!el) return null; const r = el.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight ? r : null; };
  const keep = [document.querySelector('main h1'), document.querySelector('main .price, main [class*="__price"]'), [...document.querySelectorAll('main a.button, main button.button')].find(inView)].map(inView).filter(Boolean);
  const covered = () => {
    const b = dialog.getBoundingClientRect();
    return keep.reduce((sum, r) => sum + Math.max(0, Math.min(r.right, b.right) - Math.max(r.left, b.left)) * Math.max(0, Math.min(r.bottom, b.bottom, window.innerHeight) - Math.max(r.top, b.top, 0)), 0);
  };
  let best = null;
  for (const css of places) {
    pos.textContent = css;
    const area = covered();
    if (!best || area < best.area) best = { css, area };
    if (area === 0) break;
  }
  pos.textContent = best.css;
};
const barCmp = (root) => {
  if (root.querySelector('style[data-theme-bar]')) return;
  const style = document.createElement('style');
  style.dataset.themeBar = '';
  style.textContent = CMP_BAR;
  root.append(style);
};
const watchCmp = () => {
  const cmp = document.querySelector('pandectes-cmp');
  if (!cmp || !cmp.shadowRoot) return false;
  barCmp(cmp.shadowRoot);
  if (cmp.shadowRoot.querySelector('dialog.notice[open]')) { placeCmp(cmp.shadowRoot); setTimeout(() => placeCmp(cmp.shadowRoot), 400); }
  // check at once, not next frame: Pandectes may insert the icon inside its own frame callback
  new MutationObserver(() => {
    barCmp(cmp.shadowRoot);
    // once per opened window (placing it changes the shadow root too, which would call this again)
    const open = cmp.shadowRoot.querySelector('dialog.notice[open]');
    // again after Pandectes' zoom-in, which scales the box while it runs
    if (open && open !== cmpPlacedFor) { placeCmp(cmp.shadowRoot); setTimeout(() => placeCmp(cmp.shadowRoot), 400); }
    guardFloats();
  }).observe(cmp.shadowRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
  let cmpResize = 0;
  window.addEventListener('resize', () => { clearTimeout(cmpResize); cmpResize = setTimeout(() => placeCmp(cmp.shadowRoot), 150); }, { passive: true });
  queueFloatGuard();
  return true;
};
if (!watchCmp()) {
  const findCmp = new MutationObserver(() => { if (watchCmp()) findCmp.disconnect(); });
  findCmp.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', () => setTimeout(() => findCmp.disconnect(), 10000));
}

// Product gallery: thumbnails swap the hero image.
document.querySelectorAll('[data-gallery]').forEach((gallery) => {
  const stage = gallery.querySelector('[data-gallery-hero]');
  const hero = gallery.querySelector('.gallery__hero img');
  if (!hero) return;
  const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      hero.src = thumb.dataset.galleryThumb;
      // keep responsive candidates + accessible name in sync (retro R2.1)
      if (thumb.dataset.gallerySrcset) hero.srcset = thumb.dataset.gallerySrcset;
      else hero.removeAttribute('srcset');
      if (thumb.dataset.galleryAlt) hero.alt = thumb.dataset.galleryAlt;
      gallery.querySelectorAll('.gallery__thumb').forEach((t) => t.classList.toggle('is-active', t === thumb));
      // Float belongs to the pack shots only (one per size, data-gallery-pack). The hero is one
      // <img> with a swapped src, so CSS cannot tell the images apart — this class is the tell.
      if (stage) stage.classList.toggle('is-first', thumb.hasAttribute('data-gallery-pack'));
      // phones (24.10.2026): the thumbs stand in a column beside the image that scrolls from 4 images on; a swipe to
      // image 4 or later keeps the active thumb in view (scrollIntoView would move the page too)
      const col = thumb.parentElement;
      if (col.scrollHeight > col.clientHeight + 1) col.scrollTop = thumb.offsetTop - col.offsetTop - (col.clientHeight - thumb.offsetHeight) / 2;
    });
  });
  // Phones (24.10.2026, pane pdp-mobile-2): the product fills its image box. The pack shots carry wide white margins in the
  // file (Wischkonzentrat Professional Line: about half the box was white), so each image is scaled once until the product
  // takes 92 % of the box. The margin is read from a 240 px copy on a canvas (the image CDN sends access-control-allow-origin *);
  // a tainted canvas or a failed load leaves the image as it is. `scale`/`translate` instead of `transform`, so the pdp-float
  // animation still adds on top.
  if (!stage) return;
  const PHONE = window.matchMedia('(max-width: 749px)');
  const boxes = new Map();
  const measure = (src) => new Promise((resolve) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      try {
        const w = 240;
        const h = Math.max(1, Math.round((w * im.naturalHeight) / im.naturalWidth));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(im, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h).data;
        let x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            if (d[i + 3] > 24 && (d[i] < 236 || d[i + 1] < 236 || d[i + 2] < 236)) {
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
        }
        resolve(x1 > x0 && y1 > y0 ? { x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h, ar: im.naturalWidth / im.naturalHeight } : null);
      } catch (err) {
        resolve(null);
      }
    };
    im.onerror = () => resolve(null);
    im.src = src;
  });
  const fill = async () => {
    hero.style.scale = '';
    hero.style.translate = '';
    stage.classList.remove('is-filled');
    if (!PHONE.matches) return;
    const src = hero.currentSrc || hero.src;
    if (!boxes.has(src)) boxes.set(src, measure(src));
    const box = await boxes.get(src);
    if (!box || (hero.currentSrc || hero.src) !== src) return;
    stage.classList.add('is-filled');
    const W = hero.clientWidth;
    const H = hero.clientHeight;
    const rh = Math.min(W / box.ar, H);
    const rw = rh * box.ar;
    const s = Math.max(1, Math.min((W * 0.92) / ((box.x1 - box.x0) * rw), (H * 0.92) / ((box.y1 - box.y0) * rh)));
    const dx = ((box.x0 + box.x1) / 2 - 0.5) * rw;
    const dy = ((box.y0 + box.y1) / 2 - 0.5) * rh;
    hero.style.scale = s.toFixed(3);
    hero.style.translate = `${(-dx * s).toFixed(1)}px ${(-dy * s).toFixed(1)}px`;
  };
  hero.addEventListener('load', fill);
  PHONE.addEventListener('change', fill);
  if (hero.complete) fill();
});

// Related products — Shopify recommendations require a section re-fetch.
const related = document.querySelector('[data-related][data-url]');
if (related) {
  fetch(related.dataset.url)
    .then((r) => (r.ok ? r.text() : Promise.reject()))
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-related]');
      if (fresh && fresh.querySelector('.product-card')) {
        related.innerHTML = fresh.innerHTML;
        related.hidden = false;
      }
    })
    .catch(() => {});
}

// Address form: pre-select the saved country (no-JS falls back to shop default).
document.querySelectorAll('select[data-default]').forEach((sel) => {
  if (sel.dataset.default) sel.value = sel.dataset.default;
});

// Smooth-scroll for in-page anchors (rating → reviews band).
document.querySelectorAll('a[data-scroll-to]').forEach((a) => {
  a.addEventListener('click', (e) => {
    const target = document.querySelector(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: REDUCE_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
  });
});

// Scroll-reveal — skipped entirely under prefers-reduced-motion (BFSG).
if (!REDUCE_MOTION.matches && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
  // Safety sweep: content must never stay hidden (screenshot tools, odd viewports).
  setTimeout(() => {
    document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  }, 3000);
} else {
  document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
}

// Collection filters: the form has no visible submit button (noscript only),
// so JS must apply facet changes. Checkboxes submit immediately; price
// inputs submit on change (blur/Enter).
const filterForm = document.getElementById('CollectionFilters');
if (filterForm) {
  filterForm.addEventListener('change', (e) => {
    if (e.target.matches('input[type="checkbox"], input[type="number"]')) {
      filterForm.submit();
    }
  });
}

// Back-in-stock submit state. A slow POST otherwise looks like a dead button and
// the buyer clicks again (cro/references/form.md, "Post-Submit States").
document.querySelectorAll('[data-bis-submit]').forEach((btn) => {
  btn.form?.addEventListener('submit', () => {
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = btn.dataset.sending || 'Wird gesendet …';
  });
});

// FAQ cards (sections/home-faq, snippets/faq-list): one answer open at a time, sliding open and shut (Alex
// 2026-10-15). Moved here from the home section 2026-10-16 so the home, collection and product FAQ share one
// behaviour. The script takes over the one-open rule from the details name (removed, it would close the other
// card instantly); without JS the name keeps working. Reduced motion: no slide.
document.querySelectorAll('.home-faq__list').forEach((list) => {
  if (!('animate' in Element.prototype)) return;
  const items = [...list.querySelectorAll('.home-faq__item')];
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const timing = { duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)' };
  const running = new WeakMap();
  const stop = (item) => {
    const anim = running.get(item);
    if (anim) anim.cancel();
    running.delete(item);
    item.classList.remove('is-closing');
  };
  const close = (item) => {
    const body = item.querySelector('.home-faq__answer');
    if (!item.open || item.classList.contains('is-closing')) return;
    stop(item);
    if (still.matches || !body) { item.open = false; return; }
    item.classList.add('is-closing');
    const pad = getComputedStyle(body).paddingBottom;
    const anim = body.animate([{ height: `${body.offsetHeight}px`, paddingBottom: pad, opacity: 1 }, { height: '0px', paddingBottom: '0px', opacity: 0 }], timing);
    running.set(item, anim);
    anim.onfinish = () => { running.delete(item); item.classList.remove('is-closing'); item.open = false; };
  };
  const open = (item) => {
    const body = item.querySelector('.home-faq__answer');
    stop(item);
    item.open = true;
    if (still.matches || !body) return;
    const pad = getComputedStyle(body).paddingBottom;
    const anim = body.animate([{ height: '0px', paddingBottom: '0px', opacity: 0 }, { height: `${body.offsetHeight}px`, paddingBottom: pad, opacity: 1 }], timing);
    running.set(item, anim);
    anim.onfinish = () => running.delete(item);
  };
  items.forEach((item) => {
    item.removeAttribute('name');
    item.querySelector('summary').addEventListener('click', (event) => {
      event.preventDefault();
      if (item.open && !item.classList.contains('is-closing')) { close(item); return; }
      items.filter((other) => other !== item).forEach(close);
      open(item);
    });
  });
});
